import { CameraStatusEnum, PermissionEnum, UnitTypeEnum } from '@api/enum';
import { ICamera } from '@api/models/camera.model';
import { getAvailableCameraStorageList } from '@api/services/camera.service';
import CustomSwitch from '@components/FormFields/CustomSwitch';
import { Button, Card, LinearProgress } from '@hyperflake/react-ui-library';
import { DatePicker } from '@library/datepicker';
import { FormHelperText, Label } from '@library/forms';
import { CircularProgress } from '@library/loaders/components';
import { useQuery } from '@tanstack/react-query';
import { ErrorMessage, Form, Formik } from 'formik';
import capitalize from 'lodash.capitalize';
import upperCase from 'lodash.uppercase';
import useCameraData from 'modules/cameras/hooks/useCameraData';
import useCameraUpdateForm from 'modules/cameras/hooks/useCameraUpdateForm';
import RequiredMark from 'modules/shared/components/FormFields/RequiredMark';
import { PageHeading, PageTemplate } from 'modules/shared/features/content';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { GoogleMap, useGoogleMapScriptLoader } from 'modules/shared/features/google-map';
import { resolutionOption, timezoneList } from 'modules/shared/helpers/shared.constants';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CameraBimForm from '../CameraBimForm/CameraBimForm';
import CameraPluginForm from '../CameraPluginForm/CameraPluginForm';
import ChangeProjectForm from '../ChangeProjectForm/ChangeProjectForm';
import MapCameraOverlay from 'modules/shared/features/camera-map/components/MapCameraOverlay/MapCameraOverlay';
import { toMapCameraFromTimelapse } from 'modules/shared/features/camera-map/helpers/mapCamera.adapters';
import MapCameraControlsPanel from 'modules/shared/features/camera-map/components/MapCameraControlsPanel/MapCameraControlsPanel';

interface ICameraEditProps {}

const CameraEdit = (props: ICameraEditProps) => {
    const { clientId, projectId, cameraId } = useParams();
    const cameraData = useCameraData(clientId, projectId, cameraId);
    const { camera } = cameraData;
    const cameraUpdateForm = useCameraUpdateForm({ clientId, projectId, cameraId, camera });

    const [cameraAngle, setCameraAngle] = useState(45); // Direction in degrees
    const [cameraRange, setCameraRange] = useState(100); // Range in meters
    const [cameraFOV, setCameraFOV] = useState(90); // Field of view in degrees

    // Location editing states
    const [isEditingLocation, setIsEditingLocation] = useState(false);
    const [tempLatitude, setTempLatitude] = useState('');
    const [tempLongitude, setTempLongitude] = useState('');
    const [mapClickListener, setMapClickListener] = useState<google.maps.MapsEventListener | null>(null);
    const location = useLocation();

    const { data: storages, isLoading: storageIsLoading } = useQuery<string[]>(
        ['clients', clientId, 'projects', projectId, 'cameras', 'storage_names'],
        () => getAvailableCameraStorageList(clientId as string, projectId as string)
    );

    const resOption = useMemo(() => {
        return resolutionOption.map((val) => ({
            label: JSON.stringify(val),
            value: JSON.stringify(val),
        }));
    }, []);

    // option for drop downs
    const unitTypeOption = useMemo(() => {
        const temp = Object.values(UnitTypeEnum);
        return temp.map((val) => ({
            label: capitalize(val),
            value: val,
        }));
    }, []);

    const timezoneOption = useMemo(() => {
        return [
            {
                label: 'Select Timezone',
                value: '',
            },
            ...timezoneList.map((tz) => ({
                label: tz.label,
                value: tz.value,
            })),
        ];
    }, []);

    const storageOption = useMemo(() => {
        if (!storages) return [];
        return [{ label: 'Select Storage', value: '' }, ...storages.map((val: string) => ({ label: val, value: val }))];
    }, [storages]);

    const statusOption = useMemo(() => {
        return Object.values(CameraStatusEnum).map((status) => ({
            label: upperCase(status),
            value: status,
        }));
    }, []);

    // Initialize camera view settings when camera data loads
    useEffect(() => {
        if (camera) {
            setCameraAngle(+camera.angle || 45);
            setCameraRange(+camera.distance || 100);
            setCameraFOV(+camera.fieldOfView || 90);

            if (camera?.location) {
                setTempLatitude(camera?.location?.latitude?.toString() || '');
                setTempLongitude(camera?.location?.longitude?.toString() || '');
            }
        }
    }, [camera]);

    // form submit
    const handleDataTransformation = (values: any) => {
        const temp = { ...values };
        temp.folder = temp.folder.trim() === '' ? null : temp.folder.trim();
        temp.installationDate = moment(temp.installationDate).format('YYYYMMDD');
        temp.latestImage = temp.latestImage.name === '' ? null : temp.latestImage;
        return temp;
    };

    const handleCameraEdit = async (payload: any) => {
        try {
            const updatedCamera = await cameraUpdateForm.submit(payload);

            toast.success('TimeLapse Camera Updated Successfully.');
            cameraData.setData(updatedCamera);
        } catch (ex: any) {
            toast.error(ex?.data?.message || 'Some error occured, please try again.');
            console.log(ex);
        }
    };

    const handleSubmit = (values: any) => {
        const formattedData = handleDataTransformation(values);
        handleCameraEdit(formattedData);
    };

    // Handle save camera view settings
    const handleSaveCameraView = async () => {
        try {
            // Validate location if editing
            if (isEditingLocation) {
                const lat = parseFloat(tempLatitude);
                const lng = parseFloat(tempLongitude);

                if (isNaN(lat) || isNaN(lng)) {
                    toast.error('Please enter valid coordinates');
                    return;
                }
            }

            // Prepare values object similar to form values
            const values = {
                ...cameraUpdateForm.initialValues,
                location: isEditingLocation
                    ? {
                          latitude: parseFloat(tempLatitude).toString(),
                          longitude: parseFloat(tempLongitude).toString(),
                      }
                    : cameraUpdateForm.initialValues.location,
            };

            // Use the same transformation logic as form submit
            const formattedData = handleDataTransformation(values);

            // Add camera view settings (angle, distance, fieldOfView)
            const payload = {
                ...formattedData,
                angle: cameraAngle,
                distance: cameraRange,
                fieldOfView: cameraFOV,
            };

            // Call the submit function from the hook
            const updatedCamera = await cameraUpdateForm.submit(payload);

            toast.success('Camera view settings updated successfully');
            cameraData.setData(updatedCamera);

            // Reset editing state if location was changed
            if (isEditingLocation) {
                setIsEditingLocation(false);
            }
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to update camera view settings');
            console.error('Error saving camera view:', error);
        }
    };

    // Handle location change toggle
    const handleLocationChange = () => {
        if (isEditingLocation) {
            // Just toggle off and update preview
            const lat = parseFloat(tempLatitude);
            const lng = parseFloat(tempLongitude);

            if (!isNaN(lat) && !isNaN(lng)) {
                // Update the camera data locally for preview
                if (camera) {
                    const updatedCamera = {
                        ...camera,
                        location: {
                            ...camera.location,
                            latitude: lat.toString(),
                            longitude: lng.toString(),
                        },
                    };

                    cameraData.setData(updatedCamera);
                    // toast.info('Location updated. Click "Save" to persist changes.');
                }
            } else {
                toast.error('Please enter valid coordinates');
                return;
            }
        }
        setIsEditingLocation(!isEditingLocation);
    };

    // Handle map click to select location
    const handleMapClick = useCallback(
        (e: google.maps.MapMouseEvent) => {
            if (!isEditingLocation || !e.latLng) return;

            const lat = e.latLng.lat();
            const lng = e.latLng.lng();

            setTempLatitude(lat.toFixed(6));
            setTempLongitude(lng.toFixed(6));

            toast.info('Location selected on map');
        },
        [isEditingLocation]
    );

    // Handle draggable marker position change
    const handleMarkerDragEnd = useCallback((position: google.maps.LatLngLiteral) => {
        setTempLatitude(position.lat.toFixed(6));
        setTempLongitude(position.lng.toFixed(6));
        toast.info('Marker position updated');
    }, []);

    const { hasPermission } = useAuthPermission();

    const { isLoaded, error } = useGoogleMapScriptLoader();

    const cameraPosition = useMemo(() => {
        if (!camera?.location) return null;

        return {
            lat: +camera.location.latitude,
            lng: +camera.location.longitude,
        };
    }, [camera]);

    // Temporary marker position for location editing
    const tempMarkerPosition = useMemo(() => {
        if (!isEditingLocation) return null;

        const lat = parseFloat(tempLatitude);
        const lng = parseFloat(tempLongitude);

        if (!isNaN(lat) && !isNaN(lng)) {
            return { lat, lng };
        }

        return null;
    }, [isEditingLocation, tempLatitude, tempLongitude]);

    const mapBounds = useMemo(() => {
        if (!camera?.location) return null;
        return {
            center: {
                latitude: +camera.location.latitude,
                longitude: +camera.location.longitude,
            },
            zoom: 15,
        };
    }, [camera]);

    const handleLoad = useCallback(
        (map: google.maps.Map) => {
            if (!mapBounds) return;

            // Add click listener for location selection
            map.addListener('click', handleMapClick);

            map.addListener('click', (e: any) => {
                if (import.meta.env.VITE_APP_ENVIRONMENT === 'staging') {
                    console.log({ zoom: map.getZoom(), ...e.latLng.toJSON() });
                }
            });

            setTimeout(() => {
                map.setZoom(mapBounds.zoom);
            }, 1000);
        },
        [mapBounds, handleMapClick]
    );

    // Cleanup map listener on unmount
    useEffect(() => {
        return () => {
            if (mapClickListener) {
                google.maps.event.removeListener(mapClickListener);
            }
        };
    }, [mapClickListener]);

    useEffect(() => {
        const shouldScroll = location.hash === '#map-preview' || (location.state as any)?.focus === 'map-preview';
        if (!shouldScroll) return;

        let tries = 0;
        const tick = () => {
            const el = document.getElementById('map-preview');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }
            tries += 1;
            if (tries < 20) setTimeout(tick, 100); // retry up to ~2s
        };

        tick();
    }, [location.hash, location.state]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
        if (!camera) return false;
        return (
            cameraAngle !== (+camera?.angle || 45) ||
            cameraRange !== (+camera?.distance || 100) ||
            cameraFOV !== (+camera?.fieldOfView || 90) ||
            isEditingLocation
        );
    }, [camera, cameraAngle, cameraRange, cameraFOV, isEditingLocation]);

    // if (error) {
    //     return <div>Error loading Google Maps: {error.message}</div>;
    // }

    if (!isLoaded || !cameraPosition) {
        return (
            <div className="fixed inset-0 flex items-center justify-center">
                <CircularProgress />
            </div>
        );
    }

    return (
        <PageTemplate>
            <PageHeading>Edit Camera</PageHeading>
            {cameraData.isLoading ? (
                <LinearProgress color="primary" />
            ) : (
                <div>
                    <Formik
                        initialValues={cameraUpdateForm.initialValues}
                        enableReinitialize
                        validationSchema={cameraUpdateForm.schema}
                        onSubmit={handleSubmit}
                    >
                        {(formik) => (
                            <Form>
                                <fieldset disabled={!hasPermission(PermissionEnum.TIMELAPSE_CAMERA_UPDATE)}>
                                    <div className="">
                                        <Card className="mt-4">
                                            <Card.Header>
                                                <h6>Basic Details</h6>
                                            </Card.Header>
                                            <Card.Body>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {/* id */}
                                                    <div>
                                                        <Label>ID</Label>
                                                        <div className="flex gap-2">
                                                            <FormikInput name="_id" disabled />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="_id" />
                                                        </FormHelperText>
                                                    </div>
                                                    {/* name */}
                                                    <div className="">
                                                        <Label>
                                                            Name <RequiredMark />
                                                        </Label>
                                                        <FormikInput type="text" name="name" />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="name" />
                                                        </FormHelperText>
                                                    </div>
                                                    {/* installation Date */}
                                                    <div className="">
                                                        <Label>
                                                            Installation Date <RequiredMark />
                                                        </Label>
                                                        <DatePicker
                                                            className="flex items-center w-full font-medium bg-transparent border border-grayscale-300 placeholder:text-grayscale-400 focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10 text-sm px-4 h-10 rounded-md"
                                                            onChange={(val) =>
                                                                formik.setFieldValue('installationDate', val)
                                                            }
                                                            value={formik.values.installationDate}
                                                            maxDate={new Date()}
                                                        />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="installationDate" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* camera/unit type */}
                                                    <div>
                                                        <Label>
                                                            Type <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect
                                                                name="details.type"
                                                                options={unitTypeOption}
                                                                disabled
                                                            />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="details.type" />
                                                        </FormHelperText>
                                                    </div>
                                                    {/* camera/unit version */}
                                                    <div className="">
                                                        <Label>Version</Label>
                                                        <FormikInput type="text" name="details.version" disabled />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="details.version" />
                                                        </FormHelperText>
                                                    </div>
                                                    {/* timezone */}
                                                    <div>
                                                        <Label>Timezone</Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect
                                                                name="timezone"
                                                                options={timezoneOption}
                                                                disabled
                                                            />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="timezone" />
                                                        </FormHelperText>
                                                    </div>
                                                    {/* orientation */}
                                                    <div className="">
                                                        <Label>Orientation</Label>
                                                        <FormikInput type="text" name="orientation" disabled />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="orientation" />
                                                        </FormHelperText>
                                                    </div>
                                                    {/* storage */}
                                                    <div>
                                                        <Label>
                                                            Storage <RequiredMark />
                                                        </Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect name="storage" options={storageOption} />
                                                            {storageIsLoading && <CircularProgress size={14} />}
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="storage" />
                                                        </FormHelperText>
                                                    </div>
                                                    {/* status */}
                                                    <div>
                                                        <Label>
                                                            Status <RequiredMark />
                                                        </Label>
                                                        <FormikSelect name="status" options={statusOption} />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="status" />
                                                        </FormHelperText>
                                                    </div>
                                                    <div>
                                                        <Label>Image Name</Label>
                                                        <FormikInput type="text" name="latestImage.name" />
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="latestImage.name" />
                                                        </FormHelperText>
                                                    </div>

                                                    <div>
                                                        <Label>Resolution</Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect
                                                                name="latestImage.resolution"
                                                                options={resOption}
                                                            />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="latestImage.resolution" />
                                                        </FormHelperText>
                                                    </div>

                                                    {/* project */}
                                                    <div>
                                                        <Label>Folder</Label>
                                                        <div className="flex gap-2">
                                                            <FormikInput name="folder" disabled />
                                                        </div>
                                                        <FormHelperText variant={'error'}>
                                                            <ErrorMessage name="folder" />
                                                        </FormHelperText>
                                                        <CustomSwitch name="isPointedTo" label="Is Pointed" disabled />
                                                    </div>
                                                </div>
                                                <div className="mt-4">
                                                    <h5>Location</h5>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                                        <div className="">
                                                            <Label>Latitude</Label>
                                                            <FormikInput type="text" name="location.latitude" />
                                                            <FormHelperText variant={'error'}>
                                                                <ErrorMessage name="location.latitude" />
                                                            </FormHelperText>
                                                        </div>
                                                        <div className="">
                                                            <Label>Longitude</Label>
                                                            <FormikInput type="text" name="location.longitude" />
                                                            <FormHelperText variant={'error'}>
                                                                <ErrorMessage name="location.longitude" />
                                                            </FormHelperText>
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="submit"
                                                    size={'lg'}
                                                    className="mt-4 w-28"
                                                    disabled={cameraUpdateForm.isSubmitting}
                                                >
                                                    {cameraUpdateForm.isSubmitting ? (
                                                        <CircularProgress size={20} color={'inherit'} />
                                                    ) : (
                                                        'Save'
                                                    )}
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </div>
                                </fieldset>
                            </Form>
                        )}
                    </Formik>
                    <Card className="mt-4">
                        <Card.Header>
                            <h6>Plugins </h6>
                        </Card.Header>
                        <Card.Body>
                            <>
                                <CameraPluginForm
                                    camera={camera}
                                    clientId={clientId}
                                    projectId={projectId}
                                    onCameraUpdate={(updatedCamera: ICamera) => cameraData.setData(updatedCamera)}
                                />
                                <div className="mt-4 pt-4 border-t ">
                                    <CameraBimForm
                                        camera={camera}
                                        clientId={clientId}
                                        projectId={projectId}
                                        onCameraUpdate={(updatedCamera: ICamera) => cameraData.setData(updatedCamera)}
                                    />
                                </div>
                            </>
                        </Card.Body>
                    </Card>
                    <Card className="mt-4">
                        <Card.Header>
                            <h6>Change Project</h6>
                        </Card.Header>
                        <Card.Body>
                            <ChangeProjectForm camera={camera} />
                        </Card.Body>
                    </Card>
                    <Card>
                        <section id="map-preview" className="scroll-mt-24">
                            <div className="mt-4">
                                <Card.Header>
                                    <h6>Map Preview</h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className=" h-[500px] w-full overflow-hidden rounded-xl border">
                                        <div className="h-full w-full relative">
                                            <MapCameraControlsPanel
                                                isEditingLocation={isEditingLocation}
                                                tempLatitude={tempLatitude}
                                                tempLongitude={tempLongitude}
                                                onTempLatitudeChange={setTempLatitude}
                                                onTempLongitudeChange={setTempLongitude}
                                                onToggleEditOrApplyLocation={handleLocationChange}
                                                onCancelLocationEdit={() => {
                                                    setIsEditingLocation(false);
                                                    if (camera?.location) {
                                                        setTempLatitude(camera.location.latitude?.toString?.() ?? '');
                                                        setTempLongitude(camera.location.longitude?.toString?.() ?? '');
                                                    }
                                                }}
                                                hasUnsavedChanges={hasUnsavedChanges}
                                                cameraAngle={cameraAngle}
                                                cameraRange={cameraRange}
                                                cameraFOV={cameraFOV}
                                                onCameraAngleChange={setCameraAngle}
                                                onCameraRangeChange={setCameraRange}
                                                onCameraFOVChange={setCameraFOV}
                                                onSave={handleSaveCameraView}
                                                isSubmitting={cameraUpdateForm.isSubmitting}
                                            />

                                            {/* Map with cursor change when editing */}
                                            <div
                                                className="h-full w-full"
                                                style={{ cursor: isEditingLocation ? 'crosshair' : 'default' }}
                                            >
                                                <GoogleMap center={cameraPosition} zoom={10} onLoad={handleLoad}>
                                                    <MapCameraOverlay
                                                        camera={toMapCameraFromTimelapse(camera) as ICamera}
                                                        cameraAngle={cameraAngle}
                                                        cameraFOV={cameraFOV}
                                                        cameraRange={cameraRange}
                                                        isEditingLocation={isEditingLocation}
                                                        tempMarkerPosition={tempMarkerPosition}
                                                        onMarkerDragEnd={handleMarkerDragEnd}
                                                    />
                                                </GoogleMap>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Body>
                            </div>
                        </section>
                    </Card>
                </div>
            )}
        </PageTemplate>
    );
};

export default CameraEdit;
